import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  MenuItem,
  ListItemText,
  Box,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from '@mui/x-date-pickers';
import { getDepartmentsByLocation, getEmployeeOverTime } from "src/api/faceRegistration";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { getURL } from "src/utils/config";
import axios from "axios";
import Page from "src/components/Page";
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';

const EMPTY_CELL_VALUE = "N/A";
const OUTLINED_BORDER_COLOR = "rgba(0, 0, 0, 0.23)";
const FILTER_CONTROL_WIDTH = 220;
const INITIAL_VISIBLE_ROWS = 20;
const ROW_LOAD_BATCH_SIZE = 20;
const SCROLL_LOAD_THRESHOLD_PX = 80;

const EMPLOYEE_NAME_FIELDS = [
  "EmployeeName",
  "employeeName",
  "Name",
  "name",
  "employee_name",
  "UserName",
  "userName"
];

const LOCATION_FIELDS = [
  "LocationName",
  "locationName",
  "Location",
  "location",
  "location_name"
];

const DEPARTMENT_NAME_FIELDS = [
  "DepartmentName",
  "departmentName",
  "Department",
  "department",
  "department_name"
];

const OT_DAYS_FIELDS = [
  "OTDays",
  "otDays",
  "ot_days",
  "OverTimeDays",
  "overtimeDays",
  "overtime_days",
  "OT",
  "ot"
];


const OT_HOUR_FIELDS = [
  "OTHours",
  "otHours",
  "ot_hours",
  "OverTimeHours",
  "overtimeHours",
  "overtime_hours",
  "OT",
  "ot"
];

const OT_DETAILS_FIELDS = [
  "OTDetails",
  "otDetails",
  "ot_details",
  "OverTimeDetails",
  "overtimeDetails",
  "overtime_details"
];

const ENTRY_TIME_FIELDS = [
  "EntryTime",
  "entryTime",
  "entry_time",
  "InTime",
  "inTime",
  "in_time"
];

const EXIT_TIME_FIELDS = [
  "ExitTime",
  "exitTime",
  "exit_time",
  "OutTime",
  "outTime",
  "out_time"
];

const WORKED_HOURS_FIELDS = [
  "WorkedHours",
  "workedHours",
  "worked_hours",
  "Duration",
  "duration"
];

const OT_HOURS_FIELDS = [
  "OTHours",
  "otHours",
  "ot_hours",
  "OverTimeHours",
  "overtimeHours",
  "overtime_hours",
  "Overtime",
  "overtime"
];

const normalizeDepartmentOptions = (departments) =>
  (Array.isArray(departments) ? departments : (departments ? [departments] : []))
    .map((departmentItem, index) => ({
      ...departmentItem,
      Id: departmentItem?.Id ??
        departmentItem?.ID ??
        departmentItem?.id ??
        departmentItem?.DepartmentId ??
        departmentItem?.DepartmentID ??
        departmentItem?.department_id ??
        `department-${index}`,
      DepartmentName: String(
        departmentItem?.DepartmentName ??
        departmentItem?.departmentName ??
        departmentItem?.Department ??
        departmentItem?.department ??
        departmentItem?.Name ??
        departmentItem?.name ??
        ''
      ).trim(),
    }))
    .filter((departmentItem) => departmentItem.DepartmentName);

// Table columns for main page
const overtimeTableColumns = [
  {
    id: "employeeName",
    label: "Employee Name",
    width: 25,
    getValue: (item) => getFirstFieldValue(item, EMPLOYEE_NAME_FIELDS)
  },
  {
    id: "location",
    label: "Location",
    width: 20,
    getValue: (item) => getFirstFieldValue(item, LOCATION_FIELDS)
  },
  {
    id: "departmentName",
    label: "Department Name",
    width: 25,
    getValue: (item) => getFirstFieldValue(item, DEPARTMENT_NAME_FIELDS)
  },
  {
    id: "otDays",
    label: "OT Days",
    width: 20,
    getValue: (item) => {
      const otDays = getFirstFieldValue(item, OT_DAYS_FIELDS);

      if (otDays !== EMPTY_CELL_VALUE) {
        return otDays;
      }

      return getOTDetailItems(item).length || EMPTY_CELL_VALUE;
    }
  },
  {
    id: "otHours",
    label: "OT Hours",
    width: 20,
    getValue: (item) => {
      const otHours = getFirstFieldValue(item, OT_HOUR_FIELDS);

      if (otHours !== EMPTY_CELL_VALUE) {
        return otHours;
      }

      return getOTDetailItems(item).length || EMPTY_CELL_VALUE;
    }
  }
];

const getFirstFieldValue = (item, fieldNames, fallback = EMPTY_CELL_VALUE) => {
  const value = fieldNames
    .map((fieldName) => item?.[fieldName])
    .find((fieldValue) => (
      fieldValue !== null &&
      fieldValue !== undefined &&
      String(fieldValue).trim() !== ""
    ));

  return value === undefined ? fallback : value;
};

const parseArrayValue = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== "string" || value.trim() === "") {
    return [];
  }

  try {
    const parsedValue = JSON.parse(value);
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch (error) {
    return [];
  }
};

const getOTDetailItems = (item) => {
  for (const fieldName of OT_DETAILS_FIELDS) {
    const records = parseArrayValue(item?.[fieldName]);
    if (records.length > 0) {
      return records;
    }
  }

  return [];
};

const buildOTRecords = (item) => {
  const employeeName = getFirstFieldValue(item, EMPLOYEE_NAME_FIELDS);
  const location = getFirstFieldValue(item, LOCATION_FIELDS);
  const departmentName = getFirstFieldValue(item, DEPARTMENT_NAME_FIELDS);

  return getOTDetailItems(item).map((detail) => ({
    employeeName,
    location,
    departmentName,
    entryTime: getFirstFieldValue(detail, ENTRY_TIME_FIELDS),
    exitTime: getFirstFieldValue(detail, EXIT_TIME_FIELDS),
    workedHours: getFirstFieldValue(detail, WORKED_HOURS_FIELDS),
    otHours: getFirstFieldValue(detail, OT_HOURS_FIELDS),
  }));
};

const getTotalOTHours = (records) => {
  let totalSeconds = 0;
  let containsTimeFormattedValue = false;

  (records || []).forEach((record) => {
    const value = String(record?.otHours ?? '').trim();
    if (!value || value === EMPTY_CELL_VALUE) return;

    if (/^\d+:\d{1,2}(:\d{1,2})?$/.test(value)) {
      const [hours, minutes, seconds = 0] = value.split(':').map(Number);
      totalSeconds += (hours * 3600) + (minutes * 60) + seconds;
      containsTimeFormattedValue = true;
      return;
    }

    const numericHours = Number(value);
    if (Number.isFinite(numericHours)) {
      totalSeconds += numericHours * 3600;
    }
  });

  if (containsTimeFormattedValue) {
    const roundedTotalSeconds = Math.round(totalSeconds);
    const hours = Math.floor(roundedTotalSeconds / 3600);
    const minutes = Math.floor((roundedTotalSeconds % 3600) / 60);
    const seconds = roundedTotalSeconds % 60;

    return [hours, minutes, seconds]
      .map((part) => String(part).padStart(2, '0'))
      .join(':');
  }

  return String(Number((totalSeconds / 3600).toFixed(2)));
};

const isClickableOTValue = (value) => {
  if (value === EMPTY_CELL_VALUE || value === "" || value === null || value === undefined) {
    return false;
  }

  const numericValue = Number(value);
  return !Number.isFinite(numericValue) || numericValue > 0;
};

const OverTime = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [visibleRowCount, setVisibleRowCount] = useState(INITIAL_VISIBLE_ROWS);
  const [apiData, setApiData] = useState(null);
  const [locationNames, setLocationNames] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [departmentError, setDepartmentError] = useState('');
  const departmentRequestIdRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [fromDateError, setFromDateError] = useState(false);
  const [toDateError, setToDateError] = useState(false);
  
  // Date states
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();
  
  // Dialog state for OT details
  const [otDialogOpen, setOtDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [otRecords, setOtRecords] = useState([]);

  const handleFromDateChange = (date) => {
    setFromDate(date ? dayjs(date).startOf('day') : null);
    setFromDateError(false);
  };

  const handleToDateChange = (date) => {
    setToDate(date ? dayjs(date).endOf('day') : null);
    setToDateError(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      const apiUrl = getURL("employee-shifts-info/location-names?status=1");
      const AUTH_TOKEN = `${localStorage.getItem('token_type')} ${localStorage.getItem('access_token')}`;
      
      try {
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: AUTH_TOKEN,
          },
        });

        const extractedLocationNames = response.data.map(item => item.LocationName);
        setLocationNames(extractedLocationNames);
        setApiData(response.data);
      } catch (error) {
        console.error('Error:', error);
      }
    };
  
    fetchData();
  }, []);

  const updateLocation = useCallback((value) => {
    setSelectedLocation(value);
    const selectedLocationObject = apiData?.find((location) => location.LocationName === value);
    const locationId = selectedLocationObject ? selectedLocationObject.Id : null;
    setSelectedLocationId(locationId);
    if (value) {
      localStorage.setItem("NetLocation", value);
    } else {
      localStorage.removeItem("NetLocation");
    }
    setLocationError(false);
    
    setSelectedDepartments([]);
    setDepartmentOptions([]);
    setDepartmentError('');

    const requestId = departmentRequestIdRef.current + 1;
    departmentRequestIdRef.current = requestId;

    if (!locationId) {
      setDepartmentLoading(false);
      return;
    }

    setDepartmentLoading(true);
    getDepartmentsByLocation({ location_id: locationId }, (response) => {
      if (departmentRequestIdRef.current !== requestId) return;

      if (response.status === 200) {
        setDepartmentOptions(normalizeDepartmentOptions(response.data));
        setDepartmentError('');
      } else {
        setDepartmentOptions([]);
        setDepartmentError('Unable to load departments');
      }
      setDepartmentLoading(false);
    });
  }, [apiData]);

  useEffect(() => {
    const savedLocation = localStorage.getItem("NetLocation");
    // if (savedLocation && apiData) {
    //   updateLocation(savedLocation);
    // }
  }, [apiData, updateLocation]);

  const handleLocationChange = (value) => {
    updateLocation(value);
  };

  const handleDepartmentChange = (_event, value) => {
    if (!selectedLocation || departmentLoading) {
      setSelectedDepartments([]);
      return;
    }
    if (value.some((option) => option.isSelectAll)) {
      handleSelectAllDepartments();
      return;
    }
    setSelectedDepartments(value);
  };

  const handleSelectAllDepartments = () => {
    if (!departmentOptions.length) {
      setSelectedDepartments([]);
      return;
    }
    if (selectedDepartments.length === departmentOptions.length) {
      setSelectedDepartments([]);
    } else {
      setSelectedDepartments(departmentOptions);
    }
  };

  const handleSubmit = () => {
    const locationId = selectedLocationId || 0;

    setLoading(true);
    setLocationError(false);
    setFromDateError(false);
    setToDateError(false);

    if (locationId === 0 || !fromDate || !toDate) {
      if (locationId === 0) {
        setLocationError(true);
      }
      if (!fromDate) {
        setFromDateError(true);
      }
      if (!toDate) {
        setToDateError(true);
      }
      setLoading(false);
      return;
    }

    // Get department IDs for the selected departments
    const departmentIds = selectedDepartments.map(dept => dept.Id);

    const formattedFromDate = dayjs(fromDate).format('YYYY-MM-DD');
    const formattedToDate = dayjs(toDate).format('YYYY-MM-DD');


    const data = {
      location_id: locationId,
      from_date: formattedFromDate,
      to_date: formattedToDate,
      department_id: departmentIds.join(','),
    };
    
    console.log('Submitting with data:', data);
    
   getEmployeeOverTime(data, (response) => {
      if (response.status === 200) {
        setFilteredData(response.data);
        setVisibleRowCount(INITIAL_VISIBLE_ROWS);
      }
      setLoading(false);
    });
  };

  const handleExportExcel = async () => {
    const exportData = filteredData;
    const fromDateStr = dayjs(fromDate).format('YYYY-MM-DD');
    const toDateStr = dayjs(toDate).format('YYYY-MM-DD');
    const fileName = `OT_Report_${fromDateStr}_to_${toDateStr}.xlsx`;

    if (exportData.length !== 0) {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("OT Report");
      
      // Define headers with styles
      worksheet.columns = overtimeTableColumns.map((column) => ({
        header: column.label,
        key: column.id,
        width: column.width,
        alignment: { horizontal: "center" },
      }));

      // Add rows with data
      exportData.forEach((item, index) => {
        const rowData = overtimeTableColumns.reduce((acc, column) => ({
          ...acc,
          [column.id]: column.getValue(item, index)
        }), {});
        const row = worksheet.addRow(rowData);

        row.eachCell((cell) => {
          cell.alignment = { horizontal: "center" };
        });
      });

      // Apply styles to headers
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: "center" };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
    } else {
      alert("No data to export.");
    }
  };

  const handleOTDaysClick = (item) => {
    const employeeName = getFirstFieldValue(item, EMPLOYEE_NAME_FIELDS);

    if (!employeeName || employeeName === EMPTY_CELL_VALUE) {
      alert("Employee name not found");
      return;
    }

    setSelectedEmployee(item);
    setOtRecords(buildOTRecords(item));
    setOtDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setOtDialogOpen(false);
    setSelectedEmployee(null);
    setOtRecords([]);
  };

  const visibleData = filteredData.slice(0, visibleRowCount);

  const handleTableScroll = useCallback((event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight <= SCROLL_LOAD_THRESHOLD_PX;

    if (!isNearBottom) {
      return;
    }

    setVisibleRowCount((currentCount) => (
      Math.min(currentCount + ROW_LOAD_BATCH_SIZE, filteredData.length)
    ));
  }, [filteredData.length]);

  const errorHelperSx = {
    minHeight: 20,
    mt: 0.5,
  };

  const filterControlSx = (hasError = false, width = FILTER_CONTROL_WIDTH) => ({
    width,
    minWidth: width,
    '& .MuiOutlinedInput-root': {
      minHeight: 56,
      backgroundColor: 'background.paper',
      '& fieldset': {
        borderColor: hasError ? 'error.main' : OUTLINED_BORDER_COLOR,
      },
      '&:hover fieldset': {
        borderColor: hasError ? 'error.main' : 'text.primary',
      },
      '&.Mui-focused fieldset': {
        borderColor: hasError ? 'error.main' : 'primary.main',
      },
      '&.Mui-disabled fieldset': {
        borderColor: hasError ? 'error.main' : OUTLINED_BORDER_COLOR,
      },
    },
    '& .MuiFormHelperText-root': {
      minHeight: 20,
      marginLeft: 0,
      marginRight: 0,
    },
    '& .MuiInputLabel-root': {
      backgroundColor: 'background.paper',
      px: 0.5,
    },
  });

  return (
    <Page title="Overtime Report">
      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              flexWrap: 'nowrap',
              gap: 2,
              mx: 2,
              mt: 2,
              mb: 2,
              overflowX: 'auto',
              pt: 1.5,
              pb: 1,
            }}
          >
            {/* From Date Picker */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormControl sx={filterControlSx(fromDateError)}>
                <DatePicker
                  label="From Date"
                  id="from-date"
                  format="YYYY-MM-DD"
                  value={fromDate}
                  onChange={handleFromDateChange}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: fromDateError,
                    },
                  }}
                />
                <Typography color="error" variant="body2" sx={errorHelperSx}>
                  {fromDateError ? "Please select From Date" : " "}
                </Typography>
              </FormControl>
            </LocalizationProvider>

            {/* To Date Picker */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormControl sx={filterControlSx(toDateError)}>
                <DatePicker
                  label="To Date"
                  id="to-date"
                  format="YYYY-MM-DD"
                  value={toDate}
                  onChange={handleToDateChange}
                  minDate={fromDate}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: toDateError,
                    },
                  }}
                />
                <Typography color="error" variant="body2" sx={errorHelperSx}>
                  {toDateError ? "Please select To Date" : " "}
                </Typography>
              </FormControl>
            </LocalizationProvider>

            {/* Location Dropdown */}
            <FormControl sx={filterControlSx(locationError)}>
              <Autocomplete
                value={selectedLocation}
                onChange={(_event, value) => handleLocationChange(value || "")}
                options={locationNames}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Location"
                    variant="outlined"
                    error={locationError}
                    helperText={locationError ? "Please Select Location" : " "}
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                getOptionLabel={(option) => option || ""}
                noOptionsText="No locations available"
                sx={filterControlSx(locationError)}
              />
            </FormControl>

            {/* Department Dropdown */}
            <FormControl sx={filterControlSx(Boolean(departmentError))}>
              <Autocomplete
                multiple
                limitTags={1}
                renderTags={() => null}
                value={selectedDepartments}
                onChange={handleDepartmentChange}
                options={departmentOptions}
                loading={departmentLoading}
                loadingText="Loading departments..."
                noOptionsText={
                  selectedLocation
                    ? (departmentError || "No departments available")
                    : "Select location first"
                }
                getOptionLabel={(option) => option.DepartmentName || ""}
                isOptionEqualToValue={(option, value) => String(option.Id) === String(value.Id)}
                disabled={!selectedLocation || departmentLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Department"
                    variant="outlined"
                    error={Boolean(departmentError)}
                    helperText={departmentError || " "}
                    placeholder={
                      departmentLoading
                        ? "Loading departments..."
                        : !selectedLocation
                          ? "Select location first"
                          : "Select departments"
                    }
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <MenuItem {...props}>
                    <Checkbox
                      checked={
                        option.isSelectAll
                          ? departmentOptions.length > 0 && selectedDepartments.length === departmentOptions.length
                          : selected
                      }
                    />
                    <ListItemText primary={option.DepartmentName} />
                  </MenuItem>
                )}
                ListboxProps={{
                  style: {
                    maxHeight: 200,
                  },
                }}
                componentsProps={{
                  popper: {
                    placement: 'bottom-start',
                  },
                }}
                sx={filterControlSx(Boolean(departmentError))}
                filterOptions={(options) => {
                  if (!selectedLocation || departmentLoading || options.length === 0) {
                    return options;
                  }
                  const selectAllOption = {
                    Id: -1,
                    DepartmentName: 'Select All',
                    isSelectAll: true,
                  };
                  return [selectAllOption, ...options];
                }}
              />
            </FormControl>

            {/* Submit and Export Buttons */}
            <Box sx={{ display: 'flex', gap: 1, minHeight: 56, alignItems: 'center', pt: 0 }}>
              <LoadingButton type="submit" variant="contained" onClick={handleSubmit} loading={loading}>
                Submit
              </LoadingButton>

              <LoadingButton type="submit" variant="contained" onClick={handleExportExcel}>
                Export
              </LoadingButton>
            </Box>
          </Box>

          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer
              onScroll={handleTableScroll}
              sx={{ minWidth: 1100, maxHeight: '65vh', overflow: 'auto' }}
            >
              <Table stickyHeader sx={{ minWidth: 1100 }}>
                <TableHead>
                  <TableRow>
                    {overtimeTableColumns.map((column) => (
                      <TableCell key={column.id}>{column.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleData.map((item, index) => {
                    const absoluteIndex = index;

                    return (
                      <TableRow key={absoluteIndex}>
                        {overtimeTableColumns.map((column) => {
                          const value = column.getValue(item, absoluteIndex);
                          
                          // Make OT Days clickable
                          if (column.id === "otDays" && isClickableOTValue(value)) {
                            return (
                              <TableCell 
                                key={column.id}
                                sx={{ 
                                  cursor: 'pointer',
                                  color: 'primary.main',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  }
                                }}
                                onClick={() => handleOTDaysClick(item)}
                              >
                                {value}
                              </TableCell>
                            );
                          }
                          
                          return (
                            <TableCell key={column.id}>
                              {value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {filteredData.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'right' }}>
              Showing {visibleData.length} of {filteredData.length}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* OT Days Details Dialog */}
      <Dialog
        open={otDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Overtime Details
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          {selectedEmployee && (
            <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
              Employee: {getFirstFieldValue(selectedEmployee, EMPLOYEE_NAME_FIELDS)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {otRecords.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Employee Name</strong></TableCell>
                    <TableCell><strong>Location</strong></TableCell>
                    <TableCell><strong>Department Name</strong></TableCell>
                    <TableCell><strong>Entry Time</strong></TableCell>
                    <TableCell><strong>Exit Time</strong></TableCell>
                    <TableCell><strong>Worked Hours</strong></TableCell>
                    <TableCell><strong>OT Hours</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {otRecords.map((record, index) => (
                    <TableRow key={`${record.entryTime}-${index}`}>
                      <TableCell>{record.employeeName}</TableCell>
                      <TableCell>{record.location}</TableCell>
                      <TableCell>{record.departmentName}</TableCell>
                      <TableCell>{record.entryTime}</TableCell>
                      <TableCell>{record.exitTime}</TableCell>
                      <TableCell>{record.workedHours}</TableCell>
                      <TableCell>
                        <Chip 
                          label={record.otHours} 
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell colSpan={6} align="right">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Total OT Hours</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getTotalOTHours(otRecords)}
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography>No overtime records found for this employee.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
};

export default OverTime;
